'use client';

import * as Icons from "@/components/fun-ui/icons";
import { cn } from "@/lib/utils";
import { Fragment, HTMLAttributes, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Video } from "@/components/fun-ui/Video";

type NoteSwitcher = {
  resource: string;
  caption: string;
  description?: string;
  icon?: keyof typeof Icons;
};

interface NoteSwitchersProps extends HTMLAttributes<HTMLDivElement> {
  switchers?: NoteSwitcher[];
  autoplayInterval?: number;
}

const defaultFeatures: NoteSwitcher[] = [{
  "resource": "https://oss.findinai.cn/video/20240428/section-video-from-mind-map-to-outline",
  "caption": "大纲笔记",
  "icon": 'ListIcon'
}, {
  "resource": "https://oss.findinai.cn/video/20240428/section-video-from-outline-to-mind-map",
  "caption": "思维导图",
  "icon": 'MindMapIcon'
}]

export const NoteSwitchers = ({
  switchers = defaultFeatures,
  className = '',
  autoplayInterval = 4500
}: NoteSwitchersProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const refs = useMemo<RefObject<HTMLVideoElement | null>[]>(() => [], []);
  refs[0] = useRef<HTMLVideoElement | null>(null);
  refs[1] = useRef<HTMLVideoElement | null>(null);

  const switchTo = useCallback((index: 0 | 1) => {
    setCurrentIndex(index);
    const form = refs[index ^ 1].current;
    const to = refs[index].current;
    if (form && to) {
      const duration = Number.isFinite(form.duration) ? form.duration : 0;
      const currentTime = Number.isFinite(form.currentTime) ? form.currentTime : 0;
      if (duration > 0) {
        to.currentTime = Math.max(0, duration - currentTime);
      }
      void to.play().catch(() => {
        // Browsers may pause background media before the switcher reaches view.
      });
      form.pause();
    }
  }, [refs]);

  const holdLastFrame = useCallback((video: HTMLVideoElement) => {
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    if (duration > 0) {
      video.currentTime = Math.max(0, duration - 0.05);
    }
    video.pause();
  }, []);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      switchTo((currentIndex ^ 1) as (0 | 1));
    }, autoplayInterval);
  }, [autoplayInterval, currentIndex, switchTo]);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentIndex, autoplayInterval, switchTo, startTimer]);

  return (
    <div className={
      cn(
        "h-fit flex flex-col items-center justify-center",
        className
      )
    }>
      <div className="h-full w-full my-14 relative min-h-[620px]">
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
          {
            switchers.map((switcher, index) => (
              <div
                key={index}
                aria-hidden={index !== currentIndex}
                className={cn(
                  "absolute top-0 left-0 w-full h-full transition-opacity duration-300",
                  index === currentIndex
                    ? "z-[2] opacity-100"
                    : "z-[1] opacity-0 pointer-events-none",
                )}
              >
                <div className="rounded-[20px] shadow-[0_9px_28px_0_rgba(78,89,105,0.1)]">
                  <Video
                    ref={refs[index]}
                    loop={false}
                    className="rounded-[20px]"
                    mp4Src={`${switcher.resource}.mp4`}
                    webmSrc={`${switcher.resource}.webm`}
                    fallbackSrc={`${switcher.resource}.gif`}
                    lazy
                    playWhenVisible
                    onEnded={(event) => {
                      holdLastFrame(event.currentTarget);
                    }}
                  />
                </div>
              </div>
            ))
          }
        </div>
      </div>
      <div
        className="flex items-center relative text-lg text-foreground border border-annotation-foreground h-[50px] w-[324px] px-3 rounded-full"
      >
        {
          switchers.map((switcher, index) => {
            const Icon = (switcher.icon ? Icons[switcher.icon] : null) || Fragment;
            return (
              <div
                key={index}
                data-switcher-index={index}
                className={[
                  "w-1/2 h-full flex items-center justify-center z-[2] cursor-pointer select-none",
                  "transition-all duration-300",
                  currentIndex === index && "text-background font-medium",
                ].join(' ')}
                onClick={() => {
                  if (currentIndex === index) {
                    return;
                  }
                  switchTo(index as (0 | 1));
                  if (timerRef.current) {
                    clearInterval(timerRef.current);
                    startTimer();
                  }
                }}
              >
                <Icon size={20} />
                <span>{switcher.caption}</span>
              </div>
            )
          })
        }
        <div
          className="absolute w-[167px] -left-[1px] -top-[1px] -bottom-[1px] border border-primary bg-primary rounded-full transition-all duration-300 z-[1]"
          style={{ transform: `translateX(${currentIndex * 157}px)` }}
        ></div>
      </div>
    </div>
  );
};
